/**
 * Blocks the rest of the app while open; loads structured guides from /page-help/guides.json.
 */
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import type { PageHelpGuideEntry, PageHelpGuideSection } from '@/types/pageHelpGuide';
import { getPageHelpDisplayTitle, usePageHelpStore } from '@/store/pageHelpStore';

let guidesCache: PageHelpGuideEntry[] | null = null;

/** Fixed image height in the carousel; width follows aspect ratio. */
const CAROUSEL_IMAGE_HEIGHT_PX = 360;

function findGuide(entries: PageHelpGuideEntry[], pageTitle: string, tabTitle: string | null): PageHelpGuideEntry | undefined {
  return entries.find((g) => {
    if (g.page !== pageTitle) {
      return false;
    }
    const guideTab = g.tab ?? null;
    if (guideTab === null) {
      return tabTitle === null || tabTitle === '';
    }
    return guideTab === tabTitle;
  });
}

function sectionHeading(section: PageHelpGuideSection): string {
  return section.id === 0 ? section.title : `${section.id}. ${section.title}`;
}

export const PageHelpModal: React.FC = () => {
  const modalOpen = usePageHelpStore((s) => s.modalOpen);
  const closePageHelpModal = usePageHelpStore((s) => s.closePageHelpModal);
  const pageTitle = usePageHelpStore((s) => s.pageTitle);
  const tabTitle = usePageHelpStore((s) => s.tabTitle);

  const headline = getPageHelpDisplayTitle(pageTitle, tabTitle);
  const [guides, setGuides] = useState<PageHelpGuideEntry[] | null>(guidesCache);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    if (guidesCache) {
      setGuides(guidesCache);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/page-help/guides.json', { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as PageHelpGuideEntry[];
        if (cancelled) {
          return;
        }
        guidesCache = data;
        setGuides(data);
        setLoadError(null);
      } catch {
        if (!cancelled) {
          setLoadError('Could not load page guide.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen]);

  const activeGuide = useMemo(() => {
    if (!guides?.length || !pageTitle) {
      return undefined;
    }
    return findGuide(guides, pageTitle, tabTitle);
  }, [guides, pageTitle, tabTitle]);

  const sortedSections = useMemo(() => {
    if (!activeGuide?.sections?.length) {
      return [];
    }
    return [...activeGuide.sections].sort((a, b) => a.id - b.id);
  }, [activeGuide]);

  useEffect(() => {
    if (modalOpen) {
      setCarouselIndex(0);
    }
  }, [modalOpen, pageTitle, tabTitle]);

  useEffect(() => {
    setCarouselIndex((i) => Math.min(i, Math.max(0, sortedSections.length - 1)));
  }, [sortedSections.length]);

  const hasRichContent = sortedSections.length > 0;
  const lastIndex = Math.max(0, sortedSections.length - 1);
  const safeIndex = Math.min(carouselIndex, lastIndex);
  const currentSection = sortedSections[safeIndex];
  const hasPhoto = Boolean(currentSection?.photo);

  const navButtonSx = { minWidth: 44, px: 1.5 };

  return (
    <Dialog
      open={modalOpen}
      onClose={closePageHelpModal}
      maxWidth={hasRichContent ? 'lg' : 'sm'}
      fullWidth
      disableScrollLock={false}
    >
      <DialogTitle sx={{ pr: 6 }}>{headline}</DialogTitle>
      {hasRichContent && currentSection ? (
        <DialogContent
          dividers
          sx={{
            pt: 2,
            pb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 2,
              minHeight: { xs: 320, sm: Math.max(320, CAROUSEL_IMAGE_HEIGHT_PX + 24) },
            }}
          >
            <Button
              variant="contained"
              color="primary"
              aria-label="Previous section"
              disabled={safeIndex <= 0}
              onClick={() => setCarouselIndex((i) => Math.max(0, i - 1))}
              sx={{ alignSelf: 'center', flexShrink: 0, ...navButtonSx }}
            >
              <ChevronLeft />
            </Button>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {hasPhoto ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    gap: 3,
                    flex: 1,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                  }}
                >
                  <Box
                    component="img"
                    src={currentSection.photo}
                    alt=""
                    sx={{
                      flexShrink: 0,
                      height: CAROUSEL_IMAGE_HEIGHT_PX,
                      width: 'auto',
                      maxWidth: { xs: '100%', sm: 'min(48%, 420px)' },
                      objectFit: 'contain',
                      alignSelf: { xs: 'center', md: 'flex-start' },
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: { xs: '100%', md: 200 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      textAlign: 'left',
                    }}
                  >
                    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                      {sectionHeading(currentSection)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>
                      {currentSection.text}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    px: { xs: 1, sm: 4 },
                  }}
                >
                  <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                    {sectionHeading(currentSection)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 560, whiteSpace: 'pre-line' }}>
                    {currentSection.text}
                  </Typography>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center', flexShrink: 0 }}>
                {safeIndex + 1} / {sortedSections.length}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              aria-label="Next section"
              disabled={safeIndex >= lastIndex}
              onClick={() => setCarouselIndex((i) => Math.min(lastIndex, i + 1))}
              sx={{ alignSelf: 'center', flexShrink: 0, ...navButtonSx }}
            >
              <ChevronRight />
            </Button>
          </Box>
        </DialogContent>
      ) : (
        <>
          {loadError ? (
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {loadError}
              </Typography>
            </DialogContent>
          ) : null}
        </>
      )}
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        <Button onClick={closePageHelpModal} variant="contained" color="primary" autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
